import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Container } from "@/components/Container";
import { usePersonasQueryNoPublic } from "@/api/personas";
import { useSelectedPersona } from "@/hooks/use-selected-persona";
import { Label } from "@/components/ui/label";
import {
  Check,
  ChevronsUpDown,
  Twitter as TwitterIcon,
  Image,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useTabUrlState } from "@/hooks/use-tab-url-state";
import RepliesPage from "@/components/RepliesPage/index";
import PostPageContainer from "@/components/PostsPage/PostPageContainer";

import ContentReviewer from "@/views/ContentReviewer";

const Tab = {
  Twitter: "twitter",
  Visuals: "visuals",
} as const;
type Tab = (typeof Tab)[keyof typeof Tab];

const TABS = Object.values(Tab);

const EmptyStateModal = ({ tab }: { tab: Tab }) => {
  const navigate = useNavigate();

  const modalContent = {
    twitter: {
      title: "Create an Agent to Manage Twitter Content",
      description:
        "Create and manage Twitter posts and replies. To start managing content, you'll need to create an agent first.",
      imageSrc: "/guidance-images/tweet-creation.png",
      nextRoute: "/agents/new",
      closeRoute: "/content-management#playground",
    },
    visuals: {
      title: "Create an Agent to Review Visual Content",
      description:
        "Review and manage visual content for your AI agent. To start managing visuals, you'll need to create an agent first.",
      imageSrc: "/guidance-images/image-creation.png",
      nextRoute: "/agents/new",
      closeRoute: "/content-management#visuals",
    },
  };

  const content = modalContent[tab] || modalContent["twitter"]; // Default to twitter if tab not found

  const handleClose = () => {
    navigate(content.closeRoute, { replace: true });
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="bg-black/100 border-border md:max-w-[50%] w-[40vw] h-[75vh]">
        <DialogHeader>
          <DialogTitle className="text-primary text-xl">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-lg">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 flex items-center justify-center h[20%] border-border">
          <img
            src={content.imageSrc}
            alt="Guidance"
            className="h-[45vh] object-contain"
          />
        </div>
        <div className="flex justify-center">
          <Button
            className="bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-medium px-8 py-3"
            onClick={() => navigate(content.nextRoute, { replace: true })}
          >
            Create Agent
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function ContentManagement() {
  const {
    data: personas = [],
    isError,
    isLoading: personasLoading,
  } = usePersonasQueryNoPublic();
  const [selectedPersonaId, setSelectedPersonaId] = useSelectedPersona();
  const { search } = useLocation();
  const [tab] = useTabUrlState(TABS);
  const [error, setError] = useState<string | null>(null);
  const [personaOpen, setPersonaOpen] = useState(false);

  const selectedPersona = personas.find(
    (persona) => persona.persona_id === selectedPersonaId,
  );

  // Ensure we have a selected persona
  useEffect(() => {
    if (!personasLoading && personas.length > 0) {
      if (
        !selectedPersonaId ||
        !personas.some((p) => p.persona_id === selectedPersonaId)
      ) {
        setSelectedPersonaId(personas[0].persona_id, { replace: true });
      }
    }
  }, [personas, selectedPersonaId, setSelectedPersonaId, personasLoading]);

  useEffect(() => {
    if (isError) setError("Error loading agents. Please try again later.");
  }, [isError]);

  // Show loading spinner while personas are loading
  if (personasLoading) {
    return (
      <Container>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </Container>
    );
  }

  const renderContent = () => {
    // If the user has no personas, show the empty state modal
    if (personas.length === 0) {
      return <EmptyStateModal tab={tab} />;
    }

    // If we have personas but none selected, try to select the first one
    if (!selectedPersona && personas.length > 0) {
      setSelectedPersonaId(personas[0].persona_id, { replace: true });
      return null;
    }

    // Don't render content if we don't have a selected persona
    if (!selectedPersona) {
      return null;
    }

    switch (tab) {
      case Tab.Twitter:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="col-span-1">
              <div className="bg-black border border-gray-800 rounded-lg overflow-hidden">
                <PostPageContainer
                  key={selectedPersonaId}
                  personaId={selectedPersonaId}
                  personaData={selectedPersona}
                />
              </div>
            </div>
            <div className="col-span-1">
              <div className="bg-black border border-gray-800 rounded-lg overflow-hidden">
                <RepliesPage
                  personaId={selectedPersonaId}
                  personaData={selectedPersona}
                />
              </div>
            </div>
          </div>
        );
      case Tab.Visuals:
        return (
          <div className="bg-black border border-gray-800 rounded-lg overflow-hidden">
            <ContentReviewer />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      {error && <p className="text-red-500 text-center mt-4">{error}</p>}
      <div className="flex flex-col justify-center items-center py-3">
        <div className="flex flex-row justify-center items-center gap-3 w-full max-w-xl px-4">
          <Label htmlFor="persona-selector">Agent</Label>
          <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
            <PopoverTrigger asChild>
              <Button
                id="persona-selector"
                data-testid="persona-selector"
                variant="outline"
                role="combobox"
                aria-expanded={personaOpen}
                className="w-full max-w-64"
              >
                <span className="w-full truncate">
                  {selectedPersonaId && selectedPersona
                    ? selectedPersona.name
                    : "Select agent..."}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandInput placeholder="Search agent..." />
                <CommandList>
                  <CommandEmpty>No agent found.</CommandEmpty>
                  <CommandGroup>
                    {personas
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((persona) => (
                        <CommandItem
                          key={persona.persona_id}
                          value={persona.persona_id}
                          onSelect={(value) => {
                            setPersonaOpen(false);
                            setSelectedPersonaId(value);
                          }}
                          keywords={[persona.name]}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedPersonaId === persona.persona_id
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {persona.name}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Top Navigation for the Tab */}
        <div className="flex flex-row flex-wrap justify-center gap-2 mt-8 px-4">
          <Link to={{ hash: Tab.Twitter, search }}>
            <Button
              variant={tab === Tab.Twitter ? "default" : "outline"}
              className="w-[140px] flex items-center justify-center gap-2"
            >
              <TwitterIcon className="h-4 w-4" />
              Twitter
            </Button>
          </Link>
          <Link to={{ hash: Tab.Visuals, search }}>
            <Button
              variant={tab === Tab.Visuals ? "default" : "outline"}
              className="w-[140px] flex items-center justify-center gap-2 ml-10"
            >
              <Image className="h-4 w-4" />
              Image Gallery
            </Button>
          </Link>
        </div>
      </div>
      <div className="mt-6">{renderContent()}</div>
    </Container>
  );
}

export default ContentManagement;
